import { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Search, Filter, X } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ProductCard from '../components/ProductCard';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProductsPage = () => {
  const { categoryId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [selectedCategory, setSelectedCategory] = useState(categoryId || 'all');
  const [inStockOnly, setInStockOnly] = useState(false);

  useEffect(() => {
    document.title = selectedCategory && selectedCategory !== 'all'
      ? `${categories.find(c => c.id === selectedCategory)?.name || 'Products'} | NarkOnline`
      : 'All Products | NarkOnline - Grocery Delivery South Africa';
  }, [selectedCategory, categories]);

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [selectedCategory, inStockOnly, searchParams]);

  useEffect(() => {
    if (categoryId) {
      setSelectedCategory(categoryId);
    }
  }, [categoryId]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      if (selectedCategory && selectedCategory !== 'all') {
        params.append('category_id', selectedCategory);
      }
      if (inStockOnly) {
        params.append('in_stock_only', 'true');
      }
      const search = searchParams.get('search');
      if (search) {
        params.append('search', search);
      }
      
      const response = await axios.get(`${API}/products?${params.toString()}`);
      setProducts(response.data);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      setSearchParams({ search: searchQuery });
    } else {
      setSearchParams({});
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchParams({});
  };

  const currentCategory = categories.find(c => c.id === selectedCategory);

  return (
    <div className="min-h-screen flex flex-col bg-stone-50" data-testid="products-page">
      <Navbar />
      
      <main className="flex-1">
        {/* Header */}
        <div className="bg-white border-b border-stone-200 py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <span className="text-small text-green-900">Browse</span>
            <h1 className="heading-section text-stone-900 mt-1" data-testid="page-title">
              {currentCategory ? currentCategory.name : 'All Products'}
            </h1>
            
            {/* Search and Filters */}
            <div className="mt-6 flex flex-col sm:flex-row gap-4">
              <form onSubmit={handleSearch} className="flex-1 flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                  <Input
                    type="text"
                    placeholder="Search products..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 bg-stone-50"
                    data-testid="search-input"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={clearSearch}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <Button type="submit" className="btn-primary" data-testid="search-btn">
                  Search
                </Button>
              </form>
              
              <div className="flex gap-2">
                <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                  <SelectTrigger className="w-[180px]" data-testid="category-filter">
                    <Filter className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {categories.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Button
                  variant={inStockOnly ? "default" : "outline"}
                  onClick={() => setInStockOnly(!inStockOnly)}
                  className={inStockOnly ? "bg-green-900 hover:bg-green-800" : ""}
                  data-testid="in-stock-filter"
                >
                  In Stock Only
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Products Grid */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="spinner"></div>
            </div>
          ) : products.length === 0 ? (
            <div className="empty-state py-16">
              <Search className="w-16 h-16 text-stone-300 mb-4" />
              <h3 className="text-lg font-semibold text-stone-900 mb-2">No products found</h3>
              <p className="text-stone-600">Try adjusting your search or filters</p>
            </div>
          ) : (
            <>
              <p className="text-stone-600 mb-6" data-testid="product-count">
                Showing {products.length} product{products.length !== 1 ? 's' : ''}
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6" data-testid="products-grid">
                {products.map((product, index) => (
                  <ProductCard key={product.id} product={product} index={index} />
                ))}
              </div>
            </>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ProductsPage;
